import React from "react";
import type {
    DateOnlyRange,
    DatePickerProps,
    RangeDatePickerProps,
    SingleDatePickerProps,
    WeekDatePickerProps,
} from "./DatePicker.types";
import {
    RangeDatePickerCalendar,
    SingleDatePickerCalendar,
    WeekDatePickerCalendar,
} from "./DatePickerCalendar";
import {
    areDateOnlyRangesEqual,
    areDateOnlyValuesEqual,
    DEFAULT_INPUT_FORMATS,
    formatDateRange,
    formatSingleDate,
    getCalendarOptions,
    getDefaultRangePresets,
    getDefaultSinglePresets,
    getWeekRange,
    parseDateOnly,
    parseDateRange,
    parseSingleDate,
    serializeDateOnlyRange,
    toDateOnly,
    toDateOnlyRange,
    toDateRange,
    toInputFormats,
} from "./DatePicker.lib";
import { DatePickerShell } from "./DatePickerShell";
import { PeriodSelector } from "./PeriodSelector";

const getPrimaryInputFormat = (
    inputFormat: string | readonly string[],
): string => toInputFormats(inputFormat)[0] ?? "MM/dd/yyyy";

/**
 * Range and week pickers share identical value formatting, parsing and
 * equality wiring — only the calendar component and range constraints differ.
 */
const getRangeShellProps = (
    inputFormat: string | readonly string[],
    displayFormat: string,
) => ({
    isEqual: areDateOnlyRangesEqual,
    formatValue: (range: DateOnlyRange | undefined) =>
        formatDateRange(range, displayFormat),
    formatValueForEditing: (range: DateOnlyRange | undefined) =>
        formatDateRange(range, getPrimaryInputFormat(inputFormat)),
    parseValue: (input: string) => parseDateRange(input, inputFormat),
    serializeValue: serializeDateOnlyRange,
    emptyValue: undefined,
    inputFormat,
});

const renderSingleDatePicker = ({
    mode,
    value,
    onValueChange,
    presets,
    variant = "input",
    placeholder = "Select date",
    inputFormat = DEFAULT_INPUT_FORMATS,
    displayFormat = "MMM d, yyyy",
    weekStartsOn = 0,
    closeOnSelect = true,
    ...props
}: SingleDatePickerProps) => {
    const today = new Date();
    const calendarOptions = getCalendarOptions(
        { ...props, mode },
        weekStartsOn,
    );
    const primaryInputFormat = getPrimaryInputFormat(inputFormat);

    return (
        <DatePickerShell
            {...props}
            value={value}
            onValueChange={onValueChange}
            isEqual={areDateOnlyValuesEqual}
            formatValue={(date) => formatSingleDate(date, displayFormat)}
            formatValueForEditing={(date) =>
                formatSingleDate(date, primaryInputFormat)
            }
            parseValue={(input) => parseSingleDate(input, inputFormat)}
            serializeValue={(date) => date ?? ""}
            renderCalendar={(date, handleChange, close) => (
                <SingleDatePickerCalendar
                    {...calendarOptions}
                    value={parseDateOnly(date)}
                    onValueChange={(nextDate) => {
                        handleChange(toDateOnly(nextDate));
                        if (
                            closeOnSelect &&
                            !props.showActions &&
                            variant !== "inline" &&
                            variant !== "embedded"
                        ) {
                            close();
                        }
                    }}
                />
            )}
            emptyValue={undefined}
            todayValue={toDateOnly(today)}
            presets={presets ?? getDefaultSinglePresets(today)}
            selectionMode={mode}
            variant={variant}
            placeholder={placeholder}
            inputFormat={inputFormat}
        />
    );
};

const renderRangeDatePicker = ({
    mode,
    value,
    onValueChange,
    presets,
    selectedPresetId,
    variant = "input",
    placeholder = "Select date range",
    inputFormat = DEFAULT_INPUT_FORMATS,
    displayFormat = "MMM d, yyyy",
    weekStartsOn = 0,
    minDays,
    maxDays,
    excludeDisabled,
    ...props
}: RangeDatePickerProps) => {
    const today = new Date();
    const calendarOptions = getCalendarOptions(
        { ...props, mode },
        weekStartsOn,
    );

    return (
        <DatePickerShell
            {...props}
            {...getRangeShellProps(inputFormat, displayFormat)}
            value={value}
            onValueChange={onValueChange}
            selectedPresetId={selectedPresetId}
            renderCalendar={(range, handleChange, close) => (
                <RangeDatePickerCalendar
                    {...calendarOptions}
                    value={toDateRange(range)}
                    onValueChange={(nextRange) => {
                        handleChange(toDateOnlyRange(nextRange));
                        if (
                            nextRange?.from &&
                            nextRange.to &&
                            !props.showActions &&
                            variant !== "inline" &&
                            variant !== "embedded"
                        ) {
                            close();
                        }
                    }}
                    minDays={minDays}
                    maxDays={maxDays}
                    excludeDisabled={excludeDisabled}
                />
            )}
            todayValue={toDateOnlyRange({ from: today, to: today })}
            presets={presets ?? getDefaultRangePresets(today, weekStartsOn)}
            selectionMode={mode}
            variant={variant}
            placeholder={placeholder}
        />
    );
};

const renderWeekDatePicker = ({
    mode,
    value,
    onValueChange,
    presets,
    selectedPresetId,
    variant = "input",
    placeholder = "Select week",
    inputFormat = DEFAULT_INPUT_FORMATS,
    displayFormat = "MMM d, yyyy",
    weekStartsOn = 0,
    ...props
}: WeekDatePickerProps) => {
    const today = new Date();
    const calendarOptions = getCalendarOptions(
        { ...props, mode },
        weekStartsOn,
    );

    return (
        <DatePickerShell
            {...props}
            {...getRangeShellProps(inputFormat, displayFormat)}
            value={value}
            onValueChange={onValueChange}
            selectedPresetId={selectedPresetId}
            renderCalendar={(range, handleChange) => (
                <WeekDatePickerCalendar
                    {...calendarOptions}
                    value={toDateRange(range)}
                    onValueChange={(nextRange) =>
                        handleChange(toDateOnlyRange(nextRange))
                    }
                />
            )}
            todayValue={toDateOnlyRange(getWeekRange(today, weekStartsOn))}
            presets={presets ?? getDefaultRangePresets(today, weekStartsOn)}
            selectionMode={mode}
            variant={variant}
            placeholder={placeholder}
        />
    );
};

export const DatePicker = (props: DatePickerProps) => {
    const { mode } = props;

    if (mode === "day") {
        return renderSingleDatePicker(props);
    }
    if (mode === "range") {
        return renderRangeDatePicker(props);
    }
    if (mode === "week") {
        return renderWeekDatePicker(props);
    }
    return <PeriodSelector {...props} />;
};
